import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import type { ScrollPaginatorProps } from 'components/ScrollPaginator/ScrollPaginator';
import { flow, not } from 'fp-ts/lib/function';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePrevious } from 'utils/hooks/usePrevious';

interface InfiniteScrollItemsState<TItem> {
  items: TItem[];
  totalCount: number | null | undefined;
}

export interface InfiniteScrollItemsResult<TItem>
  extends InfiniteScrollItemsState<TItem> {
  totalCount: number;
}

interface Props<TItem> {
  /**
   * Get unique string value for each item
   *
   * This can save some reloads when using hoistedItems - this component is then able to compute
   * pagination state when new hoisted items are added (but still not when hoisted items are removed).
   */
  getItemKey?: (item: TItem) => string;
  /**
   * List of items that are shown at the top of the list
   *
   * These items may or may not be present in the list of already loaded items,
   * however be aware that further calls to loadItems MUST NOT return any already hoisted items.
   */
  hoistedItems?: TItem[];
  /**
   * Load a portion of items from given index and return them along with
   * a total count of items available for pagination
   *
   * IMPORTANT: When using hoisted items, this function MUST NOT return items that are already hoisted,
   * and also the total count must not include such items, otherwise the infinite scroll
   * won't work properly.
   */
  loadItems: (
    startIndex: number,
    limit?: number,
  ) => Awaitable<InfiniteScrollItemsResult<TItem>>;
  /**
   * React-style "deps" that trigger updating the list when they change
   */
  reloadDeps: unknown[];
}

export interface InfiniteScrollItemsHook<TItem> {
  items: TItem[];
  loadAllItems: () => Promise<void>;
  scrollPaginatorProps: ScrollPaginatorProps;
}

const initialItems: InfiniteScrollItemsState<never> = {
  items: [],
  totalCount: null,
};

interface ComputedState<TItem> {
  dirty: boolean;
  items: TItem[];
  notHoistedItems: TItem[];
  notHoistedTotal: number | null | undefined;
}

const dirtyComputedState: ComputedState<never> = {
  dirty: true,
  items: [],
  notHoistedItems: [],
  notHoistedTotal: null,
};

const prependHoisted = <TItem,>(
  hoisted: TItem[] | undefined,
  items: TItem[],
): TItem[] => (hoisted?.length ? [...hoisted, ...items] : items);

/**
 * This is a transformation of input (loaded and hoisted items) to proper values
 * while possible = while we can keep a proper list of non-hoisted items loaded from server.
 * See comments inside for details. Once this is not possible, we return dirty: true here
 * to trigger effect that reloads the list.
 */
const computeState = <TItem,>(
  loadedItems: InfiniteScrollItemsState<TItem>,
  hoistedItems: TItem[] | undefined,
  prevHoistedItems: TItem[] | undefined,
  getItemKey: ((item: TItem) => string) | undefined,
): ComputedState<TItem> => {
  if (hoistedItems === prevHoistedItems) {
    // It means loadedItems changed -> they should be valid then, containing no hoisted items
    return {
      dirty: false,
      items: prependHoisted(hoistedItems, loadedItems.items),
      notHoistedItems: loadedItems.items,
      notHoistedTotal: loadedItems.totalCount,
    };
  }

  // Otherwise list of hoisted items change, the magic follows...
  if (!getItemKey) {
    // When getItemKey is not provided, we can't do any magic and have to request reload
    // because we can't filter out hoisted items from loaded items here.
    return dirtyComputedState;
  }
  const hoistedItemKeys = new Set((hoistedItems ?? []).map(getItemKey));
  const isHoisted: (item: TItem) => boolean = flow(
    getItemKey,
    plainSet.isInSet(hoistedItemKeys),
  );
  const noHoistedRemoved = (prevHoistedItems ?? []).every(isHoisted);
  if (noHoistedRemoved) {
    // When no items were "unhoisted", we can safely remove hoisted items
    // from loaded items list and fix values like total count without the need
    // for immediate reload.
    const notHoistedItems = loadedItems.items.filter(not(isHoisted));
    const hoistedLoadedItemsCount =
      loadedItems.items.length - notHoistedItems.length;
    return {
      dirty: false,
      items: prependHoisted(hoistedItems, notHoistedItems),
      notHoistedItems,
      // We need to subtract number of loaded items that were excluded as hoisted
      // since last page load from last total count from API to get total count corresponding
      // to current filter.
      notHoistedTotal: !loadedItems.totalCount
        ? loadedItems.totalCount
        : loadedItems.totalCount - hoistedLoadedItemsCount,
    };
  }

  // When some items disappear from hoisted, we can't determine if they should appear
  // in non-hoisted list or not, therefore we must reload.
  return dirtyComputedState;
};

export const useInfiniteScrollItems = <TItem,>(
  props: Props<TItem>,
): InfiniteScrollItemsHook<TItem> => {
  const [loadedItems, setLoadedItems] =
    useState<InfiniteScrollItemsState<TItem>>(initialItems);

  const prevHoistedItems = usePrevious(props.hoistedItems);

  const firstPagePromiseRef = useRef<Promise<void> | null | 'queue-full'>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies(prevHoistedItems): triggered only when current change
  // biome-ignore lint/correctness/useExhaustiveDependencies(props.getItemKey): stable callback
  const computedState: ComputedState<TItem> = useMemo(
    () =>
      computeState(
        loadedItems,
        props.hoistedItems,
        prevHoistedItems,
        props.getItemKey,
      ),
    [loadedItems, props.hoistedItems],
  );

  const loadFirstPage = useStableCallback(async () => {
    setLoadedItems(initialItems);
    try {
      setLoadedItems(await props.loadItems(0));
    } catch (e) {
      setLoadedItems({
        ...initialItems,
        totalCount: undefined,
      });

      throw e;
    }
  });

  const loadAdditionalItems = async (limit?: number) => {
    try {
      const newItems = await props.loadItems(
        computedState.notHoistedItems.length,
        limit,
      );

      setLoadedItems((loadedItemsAfter) => {
        if (loadedItemsAfter !== loadedItems) {
          // items were updated otherwise during loading of next page -> discard loaded next page
          return loadedItemsAfter;
        }
        if (newItems.items.length === 0) {
          // Failsafe when item counts get inconsistent:
          // Every loadNextPage should return at least one item. If not,
          // force overwrite totalCount to number of currently loaded items
          // to stop further loading.
          return {
            totalCount: computedState.notHoistedItems.length,
            items: computedState.notHoistedItems,
          };
        }
        return {
          totalCount: newItems.totalCount,
          items: [...computedState.notHoistedItems, ...newItems.items],
        };
      });
    } catch (e) {
      setLoadedItems({
        items: loadedItems.items,
        totalCount: undefined,
      });

      throw e;
    }
  };

  const loadNextPage = useStableCallback(
    async () => await loadAdditionalItems(),
  );

  const loadAllItems = useStableCallback(
    async () =>
      await loadAdditionalItems(
        computedState.notHoistedTotal ?? Number.MAX_SAFE_INTEGER,
      ),
  );

  // reload content whenever dirty comes true
  const dirtyDep = useRef(0);
  if (computedState.dirty) {
    dirtyDep.current += 1;
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: finely-crafted in past, did not investigate now
  useEffect(() => {
    switch (firstPagePromiseRef.current) {
      case null:
        firstPagePromiseRef.current = loadFirstPage();
        break;
      case 'queue-full':
        // just discard
        break;
      default:
        firstPagePromiseRef.current.then(() => {
          firstPagePromiseRef.current = loadFirstPage();
        });
        firstPagePromiseRef.current = 'queue-full';
    }
  }, [...props.reloadDeps, dirtyDep.current]);

  return useMemo(
    () => ({
      items: computedState.items,
      loadAllItems,
      scrollPaginatorProps: {
        loaded: computedState.notHoistedItems.length,
        onFetch: loadNextPage,
        total: computedState.notHoistedTotal,
      },
    }),
    [computedState],
  );
};
